import { useEffect, useState } from "react";
import { useUser } from "../../context/userContext";
import { getAgentDetailsByAgentId, getAgentsByGroupId, getAllAgentGroups } from "../../api/userApi";
import { getAllProducts } from "../../api/productApi";
import { createSales } from "../../api/salesApi";
import { convertDateStringToDateTime } from '../../utils/datetimeUtils';
import { useNavigate } from "react-router-dom";
import handleAgentName from "../../utils/userUtils";
import GenericSalesForm from "../common/genericSalesForm";

export default function CreateSalesForm() {
    const navigate = useNavigate();

    const [successMsg, setSuccessMsg] = useState("");

    const { user } = useUser();
    const userId = user?.userId;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // ===== Agent Details States =====
    const [agentGroup, setAgentGroup] = useState();
    const [agentName, setAgentName] = useState();
    const [coBrokeGroupOptions, setCoBrokeGroupOptions] = useState([]);
    const [coBrokeNameOptions, setCoBrokeNameOptions] = useState([]);
    const [selectedCoBrokeGroupId, setSelectedCoBrokeGroupId] = useState();
    const [selectedCoBrokeAgentId, setSelectedCoBrokeAgentId] = useState();

    // ===== Customer Details States =====
    const [customerName, setCustomerName] = useState("");
    const [customerEmail, setCustomerEmail] = useState("");
    const [customerPhone, setCustomerPhone] = useState("");
    const [customerAddress, setCustomerAddress] = useState("");

    // ===== Category and Product States =====
    const [selectedCategory, setSelectedCategory] = useState("RESIDENTIAL");
    const [productList, setProductList] = useState([]);

    // ===== Panel and System Size States =====
    const [panelQty, setPanelQty] = useState(0);
    const [energy, setEnergy] = useState(0);

    // ===== Payment Method States =====
    const availablePaymentMethods = [
        { label: "Cash", value: "cash" },
        { label: "Credit Card 24 Months", value: "creditCard24" },
        { label: "Credit Card 36 Months", value: "creditCard36" },
        { label: "Credit Card 60 Months", value: "creditCard60" }
    ];
    const [paymentMethod, setPaymentMethod] = useState("");

    // ===== Price States =====
    const [originalPrice, setOriginalPrice] = useState(0);
    const [canEditPrice, setCanEditPrice] = useState(false);

    // ===== Residential Matching State =====
    const [residentialMatch, setResidentialMatch] = useState(null);

    // ===== Pricing Calculation States =====
    const [finalPrice, setFinalPrice] = useState();
    const [asternDiscount, setAsternDiscount] = useState();
    const [agentDiscount, setAgentDiscount] = useState();
    const [depositOn, setDepositOn] = useState();

    useEffect(() => {
        if (!userId && userId === 0) {
            return;
        }

        const abortController = new AbortController();
        setLoading(true);
        setError(null);

        const fetchAgentDetailsByAgentId = async () => {
            try {
                const response = await getAgentDetailsByAgentId(
                    userId,
                    { signal: abortController.signal }
                );
                if (response.status === 200) {
                    setAgentName(handleAgentName(
                        response.data.firstName,
                        response.data.lastName,
                        response.data.alias
                    ));
                    setAgentGroup(response.data.groupName);
                }
            }
            catch (error) {
                if (error.name === 'AbortError' || error.name === 'CanceledError') {
                    return;
                }

                if (error.code === 'ECONNABORTED') {
                    console.error('Request timeout - server may be slow');
                    setError('Unable to load agent details. Please refresh.');
                }
                else if (error.response?.status === 401) {
                    navigate('/login');
                }
                else {
                    console.error('Error fetching agent details:', error);
                    setError('Failed to load agent details');
                }
            }
        }
        fetchAgentDetailsByAgentId();
        setLoading(false);

        return () => {
            abortController.abort();
        };

    }, [userId, navigate]);

    useEffect(() => {
        const fetchAgentGroups = async () => {
            try {
                const response = await getAllAgentGroups();
                if (response.status === 200) {
                    setCoBrokeGroupOptions(response.data);
                }
                else {
                    console.error("Unexpected response:", response);
                }
            }
            catch (error) {
                console.error("Error fetching Agent groups:", error);
            }
        }
        fetchAgentGroups();
    }, []);

    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const response = await getAllProducts();
                if (response.status === 200) {
                    setProductList(response.data);
                }
                else {
                    console.error("Unexpected response:", response);
                }
            }
            catch (error) {
                console.error("Error fetching products:", error);
            }
        }
        fetchProducts();
    }, []);

    // // ===== Calculate Energy (kWp) =====
    // useEffect(() => {
    //     if (panelQty > 0) {
    //         const calculatedSize = Number((panelQty * 0.62).toFixed(2));
    //         setEnergy(calculatedSize);
    //     } 
    //     else {
    //         setEnergy(0);
    //     }
    // }, [panelQty]);

    // ===== Set Price Editing Permission Based on Category =====
    useEffect(() => {
        // Commercial can edit price, Residential cannot
        const isCommercial = selectedCategory === "COMMERCIAL";
        setCanEditPrice(isCommercial);

        // Reset price when switching categories
        setOriginalPrice(0);
        setResidentialMatch(null);
    }, [selectedCategory]);

    // ===== RESIDENTIAL LOGIC: Auto-populate Price =====
    useEffect(() => {
        if (selectedCategory === "RESIDENTIAL" && panelQty > 0) {
            // 1. Find matching product by PanelQty and Category
            // Note: Using 'PanelQty' and 'Category' based on new schema
            const match = productList.find(p =>
                p.productCategory === "RESIDENTIAL" &&
                p.panelQty === panelQty &&
                p.energy === energy
            );
            setResidentialMatch(match);

            // 2. If match found AND payment method selected, get price from column
            if (match && paymentMethod) {
                // paymentMethod value (e.g., "Cash", "CreditCard24") matches column name
                const price = match[paymentMethod];
                setOriginalPrice(price || 0);
            }
            else if (!match) {
                // No match found for this panel quantity
                setOriginalPrice(0);
            }
        }
    }, [selectedCategory, panelQty, paymentMethod, productList]);

    // // ===== NEW: Extract Payment Methods from Product Columns =====
    // useEffect(() => {
    //     if (selectedProductId && filteredProductOptions.length > 0) {
    //         const product = filteredProductOptions.find(p => String(p.productId) === String(selectedProductId));
    //         if (product) {
    //             const paymentMethods = PAYMENT_METHOD
    //                 .filter(pm => product[pm.column] !== null && product[pm.column] !== undefined)
    //                 .map(pm => ({
    //                     value: pm.column,
    //                     label: pm.label,
    //                     price: product[pm.column]
    //                 }));

    //             setAvailablePaymentMethods(paymentMethods);
    //             // Reset payment method selection
    //             setPaymentMethod("");
    //             setSelectedProductPrice(0);
    //         }
    //     } else {
    //         setAvailablePaymentMethods([]);
    //         setPaymentMethod("");
    //     }
    // }, [selectedProductId, filteredProductOptions]);
    // // ===== NEW: Auto-populate Price When Payment Method Selected =====
    // useEffect(() => {
    //     if (paymentMethod && selectedProductId && filteredProductOptions.length > 0) {
    //         const product = filteredProductOptions.find(p => String(p.productId) === String(selectedProductId));
    //         if (product && product[paymentMethod] !== undefined) {
    //             setSelectedProductPrice(product[paymentMethod]);
    //         }
    //     }
    // }, [paymentMethod, selectedProductId, filteredProductOptions]);

    // ===== Calculate Net Price =====
    useEffect(() => {
        const oriPrice = originalPrice || 0;
        const asternDisc = asternDiscount || 0;
        const agentDisc = agentDiscount || 0;
        const afterDisc = oriPrice - asternDisc - agentDisc;

        setFinalPrice(Math.max(0, afterDisc));

    }, [originalPrice, asternDiscount, agentDiscount]);

    const handleCoBrokeGroupChange = async (event) => {
        const selectedCoBrokeGroupId = event.target.value;
        setSelectedCoBrokeGroupId(selectedCoBrokeGroupId);
        setSelectedCoBrokeAgentId("");
        if (selectedCoBrokeGroupId) {
            try {
                const response = await getAgentsByGroupId(selectedCoBrokeGroupId);
                setCoBrokeNameOptions(response.data.filter(agent => String(agent.userId) !== String(user.userId)));
            }
            catch (error) {
                console.error("Error fetching agents by group ID:", error);
            }
        }
        else {
            setCoBrokeNameOptions([]);
        }
    };

    const handleCancel = (event) => {
        event.preventDefault();
        navigate("/sales");
    }

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!customerName || !customerPhone || !customerAddress || !paymentMethod) {
            alert("Please fill in all required fields.");
            return;
        }

        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        if (customerEmail) {
            if (!emailRegex.test(customerEmail)) {
                alert("Please enter a valid email address.");
                return;
            }
        }

        // ===== Validate panel quantity =====
        if (panelQty <= 0) {
            alert("Please enter a valid number of panels.");
            return;
        }

        // ===== BLOCK submission if no residential match =====
        if (selectedCategory === "RESIDENTIAL" && !residentialMatch) {
            alert("Error: No standard package found for this panel quantity. Please enter a valid panel quantity or switch to Commercial for custom pricing.");
            return;
        }

        const phoneRegex = /^\d{10,12}$/;
        if (!phoneRegex.test(customerPhone)) {
            alert("Phone number must be between 10 and 12 digits.");
            return;
        }

        if (!originalPrice || originalPrice <= 0) {
            alert("Original price must be greater than 0.");
            return;
        }

        const newSalesInfo = {
            "agentId": user.userId,
            "coAgentId": selectedCoBrokeAgentId || null,
            "discountByAstern": asternDiscount || null,
            "discountByAgent": agentDiscount || null,
            "panelQty": panelQty,
            "energy": energy,
            "originalPrice": originalPrice,
            "finalPrice": finalPrice,
            "productCategory": selectedCategory,
            "paymentMethod": paymentMethod,
            "depositOn": convertDateStringToDateTime(depositOn) || null,
            "createdBy": user.username,
            "createCustomerRequest": {
                "customerName": customerName,
                "email": customerEmail,
                "phoneNumber": customerPhone,
                "address": customerAddress
            }
        };

        try {
            const response = await createSales(newSalesInfo);
            if (response.status === 201) {
                setSuccessMsg("Sales created successfully!");
                setTimeout(() => {
                    navigate("/sales");
                }, 1500);
            }
            else {
                alert("Sales creation failed. Please check again on the details.");
                console.error("Unexpected response:", response);
            }
        }
        catch (error) {
            if (error.response?.status === 401) {
                alert("Session expired. Please log in again.");
                return;
            }
            alert("Sales creation errored. Please check again on the details.");
            console.error("Error creating sales:", error.response?.data || error);
        }
    }

    if (loading) {
        return <div>Loading agent details...</div>;
    }

    if (error) {
        return <div className="alert alert-danger">{error}</div>;
    }

    return (
        <div className="createSalesForm-container">
            {successMsg && (
                <div className="alert alert-success text-center">
                    {successMsg}
                </div>
            )}
            <form className="createSalesForm-form">
                <GenericSalesForm
                    mode="create"
                    agentGroup={agentGroup}
                    agentName={agentName}
                    coBrokeGroupOptions={coBrokeGroupOptions}
                    coBrokeNameOptions={coBrokeNameOptions}
                    selectedCoBrokeGroupId={selectedCoBrokeGroupId}
                    setSelectedCoBrokeGroupId={setSelectedCoBrokeGroupId}
                    selectedCoBrokeAgentId={selectedCoBrokeAgentId}
                    setSelectedCoBrokeAgentId={setSelectedCoBrokeAgentId}
                    customerName={customerName}
                    setCustomerName={setCustomerName}
                    customerEmail={customerEmail}
                    setCustomerEmail={setCustomerEmail}
                    customerPhone={customerPhone}
                    setCustomerPhone={setCustomerPhone}
                    customerAddress={customerAddress}
                    setCustomerAddress={setCustomerAddress}
                    selectedCategory={selectedCategory}
                    setSelectedCategory={setSelectedCategory}
                    panelQty={panelQty}
                    setPanelQty={setPanelQty}
                    energy={energy}
                    setEnergy={setEnergy}
                    originalPrice={originalPrice}
                    setOriginalPrice={setOriginalPrice}
                    availablePaymentMethods={availablePaymentMethods}
                    paymentMethod={paymentMethod}
                    setPaymentMethod={setPaymentMethod}
                    canEditPrice={canEditPrice}
                    residentialMatch={residentialMatch}
                    finalPrice={finalPrice}
                    depositOn={depositOn}
                    setDepositOn={setDepositOn}
                    asternDiscount={asternDiscount}
                    setAsternDiscount={setAsternDiscount}
                    agentDiscount={agentDiscount}
                    setAgentDiscount={setAgentDiscount}
                    handleCoBrokeGroupChange={handleCoBrokeGroupChange}
                />
                <div className="btn-container">
                </div>
                <div className="btn-container d-flex justify-content-between">
                    <input type="submit" className="btn btn-secondary" value="Cancel" onClick={handleCancel} />
                    <input type="submit" className="btn btn-primary" value="Create" onClick={handleSubmit} />
                </div>
            </form>
        </div >
    );
}